import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Shield, QrCode, Key, CheckCircle, XCircle } from 'lucide-react';
import api from '../../lib/api';

const SecuritySettings = () => {
  const [loading, setLoading] = useState(false);
  const [twoFactorStatus, setTwoFactorStatus] = useState({ enabled: false });
  const [showSetupDialog, setShowSetupDialog] = useState(false);
  const [showDisableDialog, setShowDisableDialog] = useState(false);
  const [qrCode, setQrCode] = useState('');
  const [manualKey, setManualKey] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });

  useEffect(() => {
    fetchTwoFactorStatus();
  }, []);

  const fetchTwoFactorStatus = async () => {
    try {
      const response = await api.get('/auth/2fa/status');
      setTwoFactorStatus(response.data);
    } catch (error) {
      console.error('Error fetching 2FA status:', error);
    }
  };

  const handleSetup2FA = async () => {
    try {
      setLoading(true);
      const response = await api.post('/auth/2fa/setup');
      setQrCode(response.data.qr_code);
      setManualKey(response.data.manual_entry_key);
      setShowSetupDialog(true);
      toast.success('2FA setup initiated. Please scan the QR code.');
    } catch (error) {
      console.error('Error setting up 2FA:', error);
      toast.error('Failed to setup 2FA');
    } finally {
      setLoading(false);
    }
  };

  const handleEnable2FA = async (e) => {
    e.preventDefault();
    if (verificationCode.length !== 6) {
      toast.error('Please enter a 6-digit code');
      return;
    }

    try {
      setLoading(true);
      await api.post('/auth/2fa/enable', { code: verificationCode });
      toast.success('🎉 2FA enabled successfully! Your account is now more secure.');
      setShowSetupDialog(false);
      setVerificationCode('');
      fetchTwoFactorStatus();
    } catch (error) {
      console.error('Error enabling 2FA:', error);
      toast.error('Invalid code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDisable2FA = async (e) => {
    e.preventDefault();
    if (verificationCode.length !== 6) {
      toast.error('Please enter a 6-digit code');
      return;
    }

    try {
      setLoading(true);
      await api.post('/auth/2fa/disable', { code: verificationCode });
      toast.success('2FA disabled successfully.');
      setShowDisableDialog(false);
      setVerificationCode('');
      fetchTwoFactorStatus();
    } catch (error) {
      console.error('Error disabling 2FA:', error);
      toast.error('Invalid code. Cannot disable 2FA.');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    // Validate password match
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }

    // Validate password strength
    if (passwordForm.new_password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    try {
      setLoading(true);
      await api.post('/auth/change-password', {
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password
      });
      toast.success('Password changed successfully!');
      setPasswordForm({
        current_password: '',
        new_password: '',
        confirm_password: ''
      });
    } catch (error) {
      console.error('Error changing password:', error);
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Security Settings</h2>
        <p className="text-gray-600">Manage your account security and authentication methods</p>
      </div>

      {/* Change Password */}
      <Card>
        <CardHeader>
          <div className="flex items-center space-x-3">
            <Key className="h-6 w-6 text-emerald-600" />
            <div>
              <CardTitle>Change Password</CardTitle>
              <p className="text-sm text-gray-600 mt-1">
                Update your password regularly to keep your account secure
              </p>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleChangePassword} className="space-y-4 max-w-md">
            <div className="space-y-2">
              <Label htmlFor="current_password">Current Password</Label>
              <Input
                id="current_password"
                type="password"
                value={passwordForm.current_password}
                onChange={(e) => setPasswordForm({...passwordForm, current_password: e.target.value})}
                required
                placeholder="Enter current password"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="new_password">New Password</Label>
              <Input
                id="new_password"
                type="password"
                value={passwordForm.new_password}
                onChange={(e) => setPasswordForm({...passwordForm, new_password: e.target.value})}
                required
                placeholder="Enter new password (min 8 characters)"
                minLength={8}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirm_password">Confirm New Password</Label>
              <Input
                id="confirm_password"
                type="password"
                value={passwordForm.confirm_password}
                onChange={(e) => setPasswordForm({...passwordForm, confirm_password: e.target.value})}
                required
                placeholder="Re-enter new password"
                minLength={8}
              />
            </div>

            {passwordForm.new_password && passwordForm.new_password !== passwordForm.confirm_password && (
              <p className="text-sm text-red-600">Passwords do not match</p>
            )}

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-xs text-blue-800">
                <strong>Password Requirements:</strong>
              </p>
              <ul className="text-xs text-blue-700 mt-1 ml-4 space-y-1">
                <li>• At least 8 characters long</li>
                <li>• Mix of letters and numbers recommended</li>
                <li>• Avoid common words or personal information</li>
              </ul>
            </div>

            <Button 
              type="submit" 
              disabled={loading || passwordForm.new_password !== passwordForm.confirm_password}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {loading ? 'Updating...' : 'Change Password'}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Two-Factor Authentication */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Shield className="h-6 w-6 text-emerald-600" />
              <div>
                <CardTitle>Two-Factor Authentication (2FA)</CardTitle>
                <p className="text-sm text-gray-600 mt-1">
                  Add an extra layer of security to your account
                </p>
              </div>
            </div>
            <Badge className={twoFactorStatus.enabled ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
              {twoFactorStatus.enabled ? (
                <><CheckCircle className="h-3 w-3 mr-1" />Enabled</>
              ) : (
                <><XCircle className="h-3 w-3 mr-1" />Disabled</>
              )}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {twoFactorStatus.enabled ? (
            <>
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <p className="text-sm text-green-800 font-medium mb-2">
                  ✓ Your account is protected with 2FA
                </p>
                <p className="text-xs text-green-700">
                  You'll need your authenticator app code each time you log in.
                  {twoFactorStatus.setup_at && (
                    <span className="block mt-1">
                      Enabled on: {new Date(twoFactorStatus.setup_at).toLocaleDateString()}
                    </span>
                  )}
                </p>
              </div>
              <Button
                variant="destructive"
                onClick={() => setShowDisableDialog(true)}
              >
                Disable 2FA
              </Button>
            </>
          ) : (
            <>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800 font-medium mb-2">
                  🔐 Protect your account with 2FA
                </p>
                <p className="text-xs text-blue-700">
                  Two-factor authentication adds an extra layer of security by requiring a code from your phone in addition to your password.
                </p>
              </div>
              <div className="space-y-2">
                <p className="text-sm font-medium">Benefits:</p>
                <ul className="text-sm text-gray-600 space-y-1 ml-4">
                  <li>• Protects against password theft</li>
                  <li>• Prevents unauthorized access</li>
                  <li>• Works with Google Authenticator, Microsoft Authenticator, etc.</li>
                  <li>• Industry-standard security (TOTP)</li>
                </ul>
              </div>
              <Button
                onClick={handleSetup2FA}
                disabled={loading}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                <Shield className="h-4 w-4 mr-2" />
                Enable Two-Factor Authentication
              </Button>
            </>
          )}
        </CardContent>
      </Card>

      {/* Setup Dialog */}
      <Dialog open={showSetupDialog} onOpenChange={setShowSetupDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Set Up Two-Factor Authentication</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Step 1: Scan QR Code */}
            <div className="space-y-4">
              <div className="flex items-center space-x-2">
                <div className="bg-emerald-100 rounded-full p-2">
                  <QrCode className="h-5 w-5 text-emerald-600" />
                </div>
                <div>
                  <p className="font-medium">Step 1: Scan QR Code</p>
                  <p className="text-xs text-gray-600">Use your authenticator app</p>
                </div>
              </div>
              
              {qrCode && (
                <div className="flex justify-center bg-white p-4 border rounded-lg">
                  <img src={qrCode} alt="2FA QR Code" className="w-48 h-48" />
                </div>
              )}
              
              <div className="bg-gray-50 border rounded-lg p-3">
                <div className="flex items-center space-x-2 mb-2">
                  <Key className="h-4 w-4 text-gray-600" />
                  <p className="text-sm font-medium">Manual Entry Key</p>
                </div>
                <p className="text-xs text-gray-600 mb-2">
                  Can't scan? Enter this key manually in your app:
                </p>
                <code className="block bg-white px-3 py-2 rounded border text-sm font-mono break-all">
                  {manualKey}
                </code>
              </div>
            </div>

            {/* Step 2: Verify Code */}
            <form onSubmit={handleEnable2FA} className="space-y-4">
              <div className="flex items-center space-x-2">
                <div className="bg-emerald-100 rounded-full p-2">
                  <CheckCircle className="h-5 w-5 text-emerald-600" />
                </div>
                <div>
                  <p className="font-medium">Step 2: Verify Setup</p>
                  <p className="text-xs text-gray-600">Enter the 6-digit code</p>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Authentication Code</Label>
                <Input
                  type="text"
                  placeholder="000000"
                  value={verificationCode}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                    setVerificationCode(value);
                  }}
                  maxLength={6}
                  className="text-center text-2xl tracking-widest font-mono"
                  autoFocus
                  required
                />
                <p className="text-xs text-gray-500 text-center">
                  Enter the 6-digit code from your authenticator app
                </p>
              </div>

              <div className="flex space-x-2 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1"
                  onClick={() => {
                    setShowSetupDialog(false);
                    setVerificationCode('');
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                  disabled={loading || verificationCode.length !== 6}
                >
                  {loading ? 'Verifying...' : 'Enable 2FA'}
                </Button>
              </div>
            </form>
          </div>
        </DialogContent>
      </Dialog>

      {/* Disable Dialog */}
      <Dialog open={showDisableDialog} onOpenChange={setShowDisableDialog}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Disable Two-Factor Authentication</DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleDisable2FA} className="space-y-4">
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <p className="text-sm text-yellow-800 font-medium mb-2">
                ⚠️ Security Warning
              </p>
              <p className="text-xs text-yellow-700">
                Disabling 2FA will make your account less secure. You'll only need your password to log in.
              </p>
            </div>

            <div className="space-y-2">
              <Label>Current Authentication Code</Label>
              <Input
                type="text"
                placeholder="000000"
                value={verificationCode}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                  setVerificationCode(value);
                }}
                maxLength={6}
                className="text-center text-2xl tracking-widest font-mono"
                autoFocus
                required
              />
              <p className="text-xs text-gray-500 text-center">
                Enter your current 2FA code to confirm
              </p>
            </div>

            <div className="flex space-x-2 pt-4">
              <Button
                type="button"
                variant="outline"
                className="flex-1"
                onClick={() => {
                  setShowDisableDialog(false);
                  setVerificationCode('');
                }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="destructive"
                className="flex-1"
                disabled={loading || verificationCode.length !== 6}
              >
                {loading ? 'Disabling...' : 'Disable 2FA'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SecuritySettings;
